import {
  Box,
  Button,
  Center,
  Collapse,
  Divider,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react'
import {
  LaborEntryType,
  MedicationCategory,
  MedicationRoute,
  MedicationType,
  MedicationUnit,
} from '@hb/shared'
import { useEffect, useRef, useState } from 'react'
import { useField, useForm } from 'react-final-form'

// ─── Datetime Helpers ─────────────────────────────────────────────────────────

const tsToDatetimeLocal = (ts: number | undefined): string => {
  if (!ts) return ''
  const d = new Date(ts)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const fromDatetimeLocal = (s: string): number | undefined => {
  if (!s) return undefined
  return new Date(s).getTime()
}
import { useLaborCharting } from '../../context'

// ─── Types & Constants ────────────────────────────────────────────────────────

interface MedInfo {
  name: string
  category: MedicationCategory
  defaultDose: number
  unit: MedicationUnit
  route: MedicationRoute
  highAlert?: boolean
}

const MED_CATALOG: MedInfo[] = [
  // Induction / Augmentation
  {
    name: 'Oxytocin (Pitocin)',
    category: MedicationCategory.INDUCTION,
    defaultDose: 2,
    unit: MedicationUnit.MU_PER_MIN,
    route: MedicationRoute.IV,
    highAlert: true,
  },
  {
    name: 'Misoprostol (Cytotec)',
    category: MedicationCategory.INDUCTION,
    defaultDose: 25,
    unit: MedicationUnit.MCG,
    route: MedicationRoute.PO,
    highAlert: true,
  },
  {
    name: 'Dinoprostone (Cervidil)',
    category: MedicationCategory.INDUCTION,
    defaultDose: 10,
    unit: MedicationUnit.MG,
    route: MedicationRoute.IV,
  },
  // Pain Management
  {
    name: 'Fentanyl',
    category: MedicationCategory.PAIN_MGMT,
    defaultDose: 50,
    unit: MedicationUnit.MCG,
    route: MedicationRoute.IV,
  },
  {
    name: 'Morphine',
    category: MedicationCategory.PAIN_MGMT,
    defaultDose: 2,
    unit: MedicationUnit.MG,
    route: MedicationRoute.IV,
  },
  {
    name: 'Epidural (Bupivacaine/Fentanyl)',
    category: MedicationCategory.PAIN_MGMT,
    defaultDose: 10,
    unit: MedicationUnit.ML_PER_HR,
    route: MedicationRoute.EPIDURAL,
  },
  // Antibiotics
  {
    name: 'Penicillin G',
    category: MedicationCategory.ANTIBIOTIC,
    defaultDose: 5000,
    unit: MedicationUnit.MG,
    route: MedicationRoute.IV,
  },
  {
    name: 'Ampicillin',
    category: MedicationCategory.ANTIBIOTIC,
    defaultDose: 2000,
    unit: MedicationUnit.MG,
    route: MedicationRoute.IV,
  },
  {
    name: 'Clindamycin',
    category: MedicationCategory.ANTIBIOTIC,
    defaultDose: 900,
    unit: MedicationUnit.MG,
    route: MedicationRoute.IV,
  },
  // Emergency / Postpartum
  {
    name: 'Carboprost (Hemabate)',
    category: MedicationCategory.EMERGENCY,
    defaultDose: 250,
    unit: MedicationUnit.MCG,
    route: MedicationRoute.IM,
  },
  {
    name: 'Methergine (Methylergonovine)',
    category: MedicationCategory.EMERGENCY,
    defaultDose: 0.2,
    unit: MedicationUnit.MG,
    route: MedicationRoute.IM,
  },
  {
    name: 'Tranexamic Acid (TXA)',
    category: MedicationCategory.EMERGENCY,
    defaultDose: 1000,
    unit: MedicationUnit.MG,
    route: MedicationRoute.IV,
  },
  {
    name: 'Magnesium Sulfate',
    category: MedicationCategory.EMERGENCY,
    defaultDose: 4000,
    unit: MedicationUnit.MG,
    route: MedicationRoute.IV,
    highAlert: true,
  },
]

interface ProtocolKit {
  id: string
  label: string
  color: string
  fields: {
    medName: string
    category: MedicationCategory
    type: MedicationType
    dose: number
    unit: MedicationUnit
    route: MedicationRoute
  }
}

const PROTOCOL_KITS: ProtocolKit[] = [
  {
    id: 'gbs-pen',
    label: 'GBS+ Protocol',
    color: '#3182CE',
    fields: {
      medName: 'Penicillin G',
      category: MedicationCategory.ANTIBIOTIC,
      type: MedicationType.BOLUS,
      dose: 5000,
      unit: MedicationUnit.MG,
      route: MedicationRoute.IV,
    },
  },
  {
    id: 'gbs-clinda',
    label: 'GBS+ (PCN allergy)',
    color: '#667EEA',
    fields: {
      medName: 'Clindamycin',
      category: MedicationCategory.ANTIBIOTIC,
      type: MedicationType.BOLUS,
      dose: 900,
      unit: MedicationUnit.MG,
      route: MedicationRoute.IV,
    },
  },
  {
    id: 'pitocin-induction',
    label: 'Pitocin Induction',
    color: '#805AD5',
    fields: {
      medName: 'Oxytocin (Pitocin)',
      category: MedicationCategory.INDUCTION,
      type: MedicationType.INFUSION,
      dose: 2,
      unit: MedicationUnit.MU_PER_MIN,
      route: MedicationRoute.IV,
    },
  },
  {
    id: 'pph-kit',
    label: 'PPH Kit',
    color: '#E53E3E',
    fields: {
      medName: 'Carboprost (Hemabate)',
      category: MedicationCategory.EMERGENCY,
      type: MedicationType.BOLUS,
      dose: 250,
      unit: MedicationUnit.MCG,
      route: MedicationRoute.IM,
    },
  },
  {
    id: 'mag-sulfate',
    label: 'Mag Sulfate',
    color: '#DD6B20',
    fields: {
      medName: 'Magnesium Sulfate',
      category: MedicationCategory.EMERGENCY,
      type: MedicationType.INFUSION,
      dose: 4000,
      unit: MedicationUnit.MG,
      route: MedicationRoute.IV,
    },
  },
]

const CATEGORY_META: Record<MedicationCategory, { label: string; scheme: string }> = {
  [MedicationCategory.INDUCTION]: { label: 'Induction', scheme: 'purple' },
  [MedicationCategory.PAIN_MGMT]: { label: 'Pain Mgmt', scheme: 'blue' },
  [MedicationCategory.ANTIBIOTIC]: { label: 'Antibiotic', scheme: 'green' },
  [MedicationCategory.EMERGENCY]: { label: 'Emergency', scheme: 'red' },
}

// Increment/decrement steps per unit — mirrors BPM ±1/±5 pattern from FHR
const DOSE_ADJUSTMENTS: Record<MedicationUnit, readonly number[]> = {
  [MedicationUnit.MG]: [-100, -5, 5, 100],
  [MedicationUnit.MCG]: [-50, -5, 5, 50],
  [MedicationUnit.MU_PER_MIN]: [-4, -2, 2, 4],
  [MedicationUnit.ML_PER_HR]: [-10, -5, 5, 10],
}

const HIGH_ALERT_NAMES = new Set([
  'Oxytocin (Pitocin)',
  'Magnesium Sulfate',
  'Misoprostol (Cytotec)',
])
const PITOCIN_NAMES = new Set(['Oxytocin (Pitocin)'])

// Tachysystole: ≥5 contractions in 10 min → frequency ≤ 2 min apart
const TACHYSYSTOLE_THRESHOLD_MIN = 2

const ROUTE_META: Record<MedicationRoute, { label: string; sub: string }> = {
  [MedicationRoute.IV]: { label: 'IV', sub: 'intravenous' },
  [MedicationRoute.IM]: { label: 'IM', sub: 'intramuscular' },
  [MedicationRoute.PO]: { label: 'PO', sub: 'oral' },
  [MedicationRoute.EPIDURAL]: { label: 'Epidural', sub: 'spinal' },
}

// ─── Section Label ────────────────────────────────────────────────────────────

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Text fontSize="lg" fontFamily="hero-new" opacity={0.8} fontWeight={400}>
    {children}
  </Text>
)

// ─── Protocol Kits Bar ────────────────────────────────────────────────────────

const ProtocolKitsBar = ({ onKitApplied }: { onKitApplied: () => void }) => {
  const { change } = useForm()
  const [activeKitId, setActiveKitId] = useState<string | null>(null)

  const applyKit = (kit: ProtocolKit) => {
    Object.entries(kit.fields).forEach(([key, value]) => change(key, value))
    setActiveKitId(kit.id)
    onKitApplied()
  }

  return (
    <Flex flexFlow="column" w="100%" gap={2}>
      <SectionLabel>QUICK PROTOCOLS</SectionLabel>
      <Flex gap={2} justifyContent="center" wrap="wrap" w="100%">
        {PROTOCOL_KITS.map(kit => {
          const isActive = activeKitId === kit.id
          return (
            <Button
              key={kit.id}
              h="52px"
              px={4}
              borderRadius={10}
              variant={isActive ? 'solid' : 'outline'}
              bg={isActive ? kit.color : undefined}
              borderColor={kit.color}
              color={isActive ? 'white' : kit.color}
              _hover={{ bg: kit.color, color: 'white', opacity: 0.9 }}
              fontSize="sm"
              fontFamily="hero-new"
              fontWeight={isActive ? 600 : 500}
              onClick={() => applyKit(kit)}>
              {kit.label}
            </Button>
          )
        })}
      </Flex>
    </Flex>
  )
}

// ─── Category Selector ────────────────────────────────────────────────────────

const CategorySelector = () => {
  const { input } = useField<MedicationCategory>('category', { subscription: { value: true } })

  return (
    <Flex flexFlow="column" w="100%" gap={2}>
      <SectionLabel>CATEGORY</SectionLabel>
      <Flex gap={2} w="100%" wrap="wrap">
        {(Object.values(MedicationCategory) as MedicationCategory[]).map(cat => {
          const meta = CATEGORY_META[cat]
          const isActive = input.value === cat
          return (
            <Button
              key={cat}
              flex={1}
              minW="80px"
              h="48px"
              variant={isActive ? 'solid' : 'outline'}
              colorScheme={isActive ? meta.scheme : 'gray'}
              borderRadius={10}
              fontFamily="hero-new"
              fontWeight={isActive ? 600 : 400}
              fontSize="sm"
              onClick={() => input.onChange(cat)}>
              {meta.label}
            </Button>
          )
        })}
      </Flex>
    </Flex>
  )
}

// ─── Medication Quick Select ──────────────────────────────────────────────────

const MedQuickSelect = ({ onMedSelected }: { onMedSelected: () => void }) => {
  const { change } = useForm()
  const { input: catInput } = useField<MedicationCategory>('category', {
    subscription: { value: true },
  })
  const { input: nameInput } = useField<string>('medName', { subscription: { value: true } })

  const medsInCategory = MED_CATALOG.filter(m => m.category === catInput.value)

  const selectMed = (med: MedInfo) => {
    change('medName', med.name)
    change('dose', med.defaultDose)
    change('unit', med.unit)
    change('route', med.route)
    onMedSelected()
  }

  if (medsInCategory.length === 0) return null

  return (
    <Flex flexFlow="column" w="100%" gap={2}>
      <SectionLabel>MEDICATION</SectionLabel>
      <Flex gap={2} w="100%" wrap="wrap">
        {medsInCategory.map(med => {
          const isActive = nameInput.value === med.name
          const cat = CATEGORY_META[med.category]
          return (
            <Button
              key={med.name}
              flex={1}
              minW="120px"
              h="auto"
              minH={med.highAlert ? '60px' : '48px'}
              py={2}
              variant={isActive ? 'solid' : 'outline'}
              colorScheme={isActive ? cat.scheme : 'gray'}
              borderRadius={10}
              onClick={() => selectMed(med)}>
              <Flex flexFlow="column" align="center" gap={0.5}>
                <Text
                  fontSize="xs"
                  fontWeight={isActive ? 600 : 400}
                  fontFamily="hero-new"
                  whiteSpace="normal"
                  textAlign="center">
                  {med.name}
                </Text>
                {med.highAlert ? (
                  <Text
                    fontSize="10px"
                    fontFamily="hero-new"
                    fontWeight={600}
                    _dark={{ color: isActive ? 'red.800' : 'red.500' }}
                    color={isActive ? 'whiteAlpha.800' : 'red.500'}>
                    ⚠ HIGH ALERT
                  </Text>
                ) : null}
              </Flex>
            </Button>
          )
        })}
      </Flex>
    </Flex>
  )
}

// ─── Administration Type Toggle ───────────────────────────────────────────────

const AdminTypeToggle = () => {
  const { input } = useField<MedicationType>('type', { subscription: { value: true } })

  return (
    <Flex flexFlow="column" w="100%" gap={2}>
      <SectionLabel>ADMINISTRATION TYPE</SectionLabel>
      <Flex gap={3} w="100%">
        {(
          [
            { id: MedicationType.BOLUS, label: 'Bolus', sub: 'one-time dose', scheme: 'teal' },
            {
              id: MedicationType.INFUSION,
              label: 'Infusion',
              sub: 'continuous',
              scheme: 'blue',
            },
          ] as const
        ).map(opt => {
          const isActive = input.value === opt.id
          return (
            <Button
              key={opt.id}
              flex={1}
              h="56px"
              variant={isActive ? 'solid' : 'outline'}
              colorScheme={isActive ? opt.scheme : 'gray'}
              borderRadius={10}
              fontFamily="hero-new"
              fontWeight={isActive ? 600 : 400}
              onClick={() => input.onChange(opt.id)}>
              <Flex flexFlow="column" align="center" gap={0}>
                <Text fontSize="sm">{opt.label}</Text>
                <Text fontSize="xs" opacity={0.7}>
                  {opt.sub}
                </Text>
              </Flex>
            </Button>
          )
        })}
      </Flex>
    </Flex>
  )
}

// ─── Dose Stepper ─────────────────────────────────────────────────────────────

const DoseStepper = () => {
  const { input: doseInput } = useField<number>('dose', { subscription: { value: true } })
  const { input: unitInput } = useField<MedicationUnit>('unit', { subscription: { value: true } })

  const unit = unitInput.value || MedicationUnit.MG
  const dose = doseInput.value ?? 0
  const adjustments = DOSE_ADJUSTMENTS[unit] ?? [-5, -1, 1, 5]

  const [isEditing, setIsEditing] = useState(false)
  const [editValue, setEditValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const adjust = (delta: number) =>
    doseInput.onChange(Math.max(0, Math.round((dose + delta) * 100) / 100))

  const startEditing = () => {
    setEditValue(dose % 1 === 0 ? String(dose) : dose.toFixed(2))
    setIsEditing(true)
  }

  const commitEdit = () => {
    const parsed = parseFloat(editValue)
    if (!isNaN(parsed)) doseInput.onChange(Math.max(0, Math.round(parsed * 100) / 100))
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') commitEdit()
    if (e.key === 'Escape') setIsEditing(false)
  }

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [isEditing])

  return (
    <Flex flexFlow="column" w="100%" gap={3} align="center">
      <Flex w="100%" justify="space-between" align="center">
        <SectionLabel>DOSE</SectionLabel>
        <Text fontSize="sm" color="gray.500" _dark={{ color: 'gray.400' }} fontFamily="hero-new">
          {unit}
        </Text>
      </Flex>
      {/* Big dose display — tap to edit manually */}
      {isEditing ? (
        <Input
          ref={inputRef}
          variant="unstyled"
          type="number"
          value={editValue}
          onChange={e => setEditValue(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={handleKeyDown}
          fontSize="5xl"
          fontWeight={700}
          fontFamily="hero-new"
          textAlign="center"
          w="160px"
          borderBottom="2px solid"
          borderColor="gray.400"
          _dark={{ borderColor: 'gray.500' }}
          borderRadius={0}
          px={1}
        />
      ) : (
        <Text
          fontSize="5xl"
          fontWeight={700}
          fontFamily="hero-new"
          lineHeight={1}
          transition="all 0.15s ease"
          cursor="pointer"
          userSelect="none"
          _hover={{ opacity: 0.7 }}
          onClick={startEditing}>
          {dose % 1 === 0 ? dose : dose.toFixed(2)}
        </Text>
      )}
      <Text
        fontSize="md"
        color="gray.500"
        _dark={{ color: 'gray.400' }}
        fontFamily="hero-new"
        mt={-1}>
        {unit}
      </Text>
      <Flex gap={2} justify="center">
        {adjustments.map(delta => (
          <Button
            key={delta}
            h="44px"
            minW="58px"
            px={3}
            variant="outline"
            colorScheme={delta < 0 ? 'red' : 'green'}
            borderRadius={10}
            fontSize="md"
            fontFamily="hero-new"
            fontWeight={500}
            onClick={() => adjust(delta)}>
            {delta > 0 ? `+${delta}` : delta}
          </Button>
        ))}
      </Flex>
    </Flex>
  )
}

// ─── Unit Selector ────────────────────────────────────────────────────────────

const UnitSelector = () => {
  const { input } = useField<MedicationUnit>('unit', { subscription: { value: true } })

  return (
    <Flex flexFlow="column" w="100%" gap={2}>
      <SectionLabel>UNIT</SectionLabel>
      <Flex gap={2} w="100%">
        {(Object.values(MedicationUnit) as MedicationUnit[]).map(u => {
          const isActive = input.value === u
          return (
            <Button
              key={u}
              flex={1}
              h="44px"
              variant={isActive ? 'solid' : 'outline'}
              colorScheme={isActive ? 'teal' : 'gray'}
              borderRadius={10}
              fontFamily="hero-new"
              fontWeight={isActive ? 600 : 400}
              fontSize="sm"
              onClick={() => input.onChange(u)}>
              {u}
            </Button>
          )
        })}
      </Flex>
    </Flex>
  )
}

// ─── Route Selector ───────────────────────────────────────────────────────────

const RouteSelector = () => {
  const { input } = useField<MedicationRoute>('route', { subscription: { value: true } })

  return (
    <Flex flexFlow="column" w="100%" gap={2}>
      <SectionLabel>ROUTE</SectionLabel>
      <Flex gap={2} w="100%">
        {(Object.values(MedicationRoute) as MedicationRoute[]).map(r => {
          const meta = ROUTE_META[r]
          const isActive = input.value === r
          return (
            <Button
              key={r}
              flex={1}
              h="56px"
              variant={isActive ? 'solid' : 'outline'}
              colorScheme={isActive ? 'blue' : 'gray'}
              borderRadius={10}
              onClick={() => input.onChange(r)}>
              <Flex flexFlow="column" align="center" gap={0}>
                <Text fontSize="sm" fontWeight={isActive ? 600 : 400} fontFamily="hero-new">
                  {meta.label}
                </Text>
                <Text fontSize="xs" opacity={0.7} fontFamily="hero-new">
                  {meta.sub}
                </Text>
              </Flex>
            </Button>
          )
        })}
      </Flex>
    </Flex>
  )
}

// ─── Site Input ───────────────────────────────────────────────────────────────

const SiteInput = () => {
  const { input } = useField<string>('site', { subscription: { value: true } })

  return (
    <Flex flexFlow="column" w="100%" gap={2}>
      <SectionLabel>ADMINISTRATION SITE</SectionLabel>
      <Input
        value={input.value ?? ''}
        onChange={e => input.onChange(e.target.value)}
        placeholder="e.g. Left hand IV, Right thigh"
        fontFamily="hero-new"
        fontSize="md"
        bg="white"
        _dark={{ bg: '#2a2a2a' }}
        borderRadius={10}
        h="48px"
      />
    </Flex>
  )
}

// ─── Timing Inputs ────────────────────────────────────────────────────────────

const StartedAtInput = () => {
  const { input } = useField<number>('startedAt', { subscription: { value: true } })

  // Default to now on mount if not yet set
  useEffect(() => {
    if (!input.value) input.onChange(Date.now())
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Flex flexFlow="column" w="100%" gap={2}>
      <SectionLabel>STARTED AT</SectionLabel>
      <Input
        type="datetime-local"
        value={tsToDatetimeLocal(input.value)}
        onChange={e => {
          const ts = fromDatetimeLocal(e.target.value)
          if (ts !== undefined) input.onChange(ts)
        }}
        fontFamily="hero-new"
        fontSize="md"
        bg="white"
        _dark={{ bg: '#2a2a2a' }}
        borderRadius={10}
        h="48px"
      />
    </Flex>
  )
}

const StoppedAtInput = () => {
  const { input } = useField<number | undefined>('stoppedAt', { subscription: { value: true } })
  const { input: typeInput } = useField<MedicationType>('type', { subscription: { value: true } })

  if (typeInput.value !== MedicationType.INFUSION) return null

  return (
    <Flex flexFlow="column" w="100%" gap={2}>
      <SectionLabel>STOPPED AT</SectionLabel>
      <Input
        type="datetime-local"
        value={tsToDatetimeLocal(input.value)}
        onChange={e => {
          const ts = fromDatetimeLocal(e.target.value)
          input.onChange(ts)
        }}
        fontFamily="hero-new"
        fontSize="md"
        bg="white"
        _dark={{ bg: '#2a2a2a' }}
        borderRadius={10}
        h="48px"
      />
    </Flex>
  )
}

// ─── Dual Verification Modal ──────────────────────────────────────────────────

interface DualVerifyModalProps {
  isOpen: boolean
  medName: string
  onVerify: () => void
  onClose: () => void
}

const DualVerifyModal = ({ isOpen, medName, onVerify, onClose }: DualVerifyModalProps) => {
  const [pin, setPin] = useState('')
  const [hasError, setHasError] = useState(false)

  const handleDigit = (digit: string) => {
    if (pin.length >= 4) return
    setHasError(false)
    const next = pin + digit
    setPin(next)
    if (next.length === 4) {
      // Accept any 4-digit PIN for now — real implementation would verify against provider auth
      setTimeout(() => {
        onVerify()
        setPin('')
      }, 200)
    }
  }

  const handleBackspace = () => {
    setHasError(false)
    setPin(p => p.slice(0, -1))
  }

  const handleClose = () => {
    setPin('')
    setHasError(false)
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="sm">
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent borderRadius={16} mx={4}>
        <ModalHeader
          bg="red.50"
          _dark={{ bg: 'rgba(229,62,62,0.12)' }}
          borderTopRadius={16}
          borderBottom="1px solid"
          borderColor="red.200">
          <Text fontFamily="hero-new" color="red.600" fontSize="lg">
            ⚠ High-Alert Medication
          </Text>
          <Text fontFamily="hero-new" fontSize="sm" color="gray.500" fontWeight={400} mt={1}>
            {medName} requires dual-provider verification
          </Text>
        </ModalHeader>
        <ModalBody pt={5} pb={6}>
          <Flex flexFlow="column" align="center" gap={4}>
            <Text
              fontFamily="hero-new"
              fontSize="sm"
              color="gray.600"
              _dark={{ color: 'gray.300' }}
              textAlign="center">
              A second provider must enter their PIN to authorize this medication.
            </Text>
            {/* PIN dot display */}
            <Flex gap={3}>
              {[0, 1, 2, 3].map(i => (
                <Box
                  key={i}
                  w="48px"
                  h="48px"
                  borderRadius={10}
                  border="2px solid"
                  borderColor={hasError ? 'red.400' : pin.length > i ? 'blue.400' : 'gray.300'}
                  bg={pin.length > i ? 'blue.50' : 'white'}
                  _dark={{ bg: pin.length > i ? 'blue.900' : '#2a2a2a' }}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  transition="all 0.15s">
                  <Text
                    fontSize="2xl"
                    fontWeight={700}
                    lineHeight={1}
                    color={pin.length > i ? 'blue.500' : 'transparent'}>
                    •
                  </Text>
                </Box>
              ))}
            </Flex>
            {hasError ? (
              <Text fontSize="sm" color="red.500" fontFamily="hero-new">
                Incorrect PIN — try again
              </Text>
            ) : null}
            {/* Number pad */}
            <Box w="100%">
              {[
                ['1', '2', '3'],
                ['4', '5', '6'],
                ['7', '8', '9'],
                ['', '0', '⌫'],
              ].map((row, ri) => (
                <Flex key={ri} gap={2} mb={2} justify="center">
                  {row.map((digit, di) => (
                    <Button
                      key={di}
                      w="72px"
                      h="56px"
                      fontSize="xl"
                      fontFamily="hero-new"
                      borderRadius={12}
                      variant={digit === '' ? 'ghost' : 'outline'}
                      colorScheme={digit === '⌫' ? 'red' : 'gray'}
                      isDisabled={digit === ''}
                      onClick={() => {
                        if (digit === '⌫') handleBackspace()
                        else if (digit !== '') handleDigit(digit)
                      }}>
                      {digit}
                    </Button>
                  ))}
                </Flex>
              ))}
            </Box>
            <Button
              variant="ghost"
              colorScheme="gray"
              fontFamily="hero-new"
              fontSize="sm"
              onClick={handleClose}>
              Cancel
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}

// ─── High Alert Banner ────────────────────────────────────────────────────────

interface HighAlertBannerProps {
  isVerified: boolean
  onRequestVerify: () => void
}

const HighAlertBanner = ({ isVerified, onRequestVerify }: HighAlertBannerProps) => {
  const { input: nameInput } = useField<string>('medName', { subscription: { value: true } })
  const isHighAlert = HIGH_ALERT_NAMES.has(nameInput.value)

  return (
    <Collapse unmountOnExit in={isHighAlert}>
      <Flex
        w="100%"
        align="center"
        justify="space-between"
        gap={3}
        bg={isVerified ? 'green.50' : 'red.50'}
        _dark={{ bg: isVerified ? 'rgba(72,187,120,0.12)' : 'rgba(229,62,62,0.12)' }}
        border="1px solid"
        borderColor={isVerified ? 'green.200' : 'red.200'}
        borderRadius={12}
        px={4}
        py={3}
        mb={3}>
        <Flex flexFlow="column" gap={0.5}>
          <Text
            fontSize="sm"
            fontWeight={700}
            fontFamily="hero-new"
            color={isVerified ? 'green.600' : 'red.600'}>
            {isVerified ? '✓ Dual-Provider Verified' : '⚠ High-Alert Medication'}
          </Text>
          <Text fontSize="xs" fontFamily="hero-new" color={isVerified ? 'green.500' : 'red.500'}>
            {isVerified
              ? 'Second provider sign-off complete'
              : 'Requires second provider PIN before saving'}
          </Text>
        </Flex>
        {isVerified ? (
          <Text fontSize="xl" color="green.500">
            ✓
          </Text>
        ) : (
          <Button
            size="sm"
            colorScheme="red"
            borderRadius={8}
            fontFamily="hero-new"
            fontWeight={600}
            onClick={onRequestVerify}>
            Sign Off
          </Button>
        )}
      </Flex>
    </Collapse>
  )
}

// ─── Tachysystole Warning ─────────────────────────────────────────────────────

const TachysystoleWarning = () => {
  const { laborEntries } = useLaborCharting()
  const { input: nameInput } = useField<string>('medName', { subscription: { value: true } })
  const { input: doseInput } = useField<number>('dose', { subscription: { value: true } })
  const { input: unitInput } = useField<MedicationUnit>('unit', { subscription: { value: true } })

  const isPitocin = PITOCIN_NAMES.has(nameInput.value)
  const latestContraction = laborEntries.findLast(e => e.type === LaborEntryType.CONTRACTION)
  const contractionFrequency = (latestContraction?.data as { frequency?: number } | undefined)
    ?.frequency

  const isTachysystole =
    isPitocin &&
    contractionFrequency !== undefined &&
    contractionFrequency <= TACHYSYSTOLE_THRESHOLD_MIN

  if (!isTachysystole) return null

  return (
    <Flex
      w="100%"
      flexFlow="column"
      gap={1.5}
      bg="orange.50"
      _dark={{ bg: 'rgba(237,137,54,0.12)' }}
      border="1px solid"
      borderColor="orange.300"
      borderRadius={12}
      px={4}
      py={3}>
      <Text fontSize="sm" fontWeight={700} fontFamily="hero-new" color="orange.700">
        ⚡ Tachysystole Alert
      </Text>
      <Text fontSize="xs" color="orange.600" fontFamily="hero-new">
        High contraction frequency ({contractionFrequency} min apart) noted at current Pitocin rate
        of {doseInput.value} {unitInput.value}. Consider reducing or holding infusion.
      </Text>
    </Flex>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

export const MedicationEntry = () => {
  const { input: nameInput } = useField<string>('medName', { subscription: { value: true } })
  const [isVerified, setIsVerified] = useState(false)
  const [isVerifyOpen, setIsVerifyOpen] = useState(false)

  // Reset verification whenever the selected medication changes
  useEffect(() => {
    setIsVerified(false)
    setIsVerifyOpen(false)
  }, [nameInput.value])

  const resetVerification = () => {
    setIsVerified(false)
  }

  return (
    <Center pb={8} w="100%">
      <Flex flexFlow="column" w="100%" maxW="520px" py={4} px={2} align="center">
        <HighAlertBanner isVerified={isVerified} onRequestVerify={() => setIsVerifyOpen(true)} />
        <Flex flexFlow="column" w="100%" gap={2}>
          <TachysystoleWarning />
          <ProtocolKitsBar onKitApplied={resetVerification} />
          <Divider />
          <CategorySelector />
          <MedQuickSelect onMedSelected={resetVerification} />
          <Divider />
          <AdminTypeToggle />
          <Divider />
          <DoseStepper />
          <UnitSelector />
          <Divider />
          <RouteSelector />
          <SiteInput />
          <Divider />
          <StartedAtInput />
          <StoppedAtInput />
        </Flex>
      </Flex>

      <DualVerifyModal
        isOpen={isVerifyOpen}
        medName={nameInput.value}
        onVerify={() => {
          setIsVerified(true)
          setIsVerifyOpen(false)
        }}
        onClose={() => setIsVerifyOpen(false)}
      />
    </Center>
  )
}
